import Alpine from 'alpinejs'
import { fragment, uuid } from '@js/util/helpers.js'

// Define (mobile) menu open/close state
Alpine.store('menu', false)

// Define (mobile) secondary menu open/close state
Alpine.store('secondaryMenu', false)

// Use @media query for hovering devices
let canHover = window.matchMedia('(hover: hover) and (pointer: fine)')
Alpine.store('canHover', () => canHover.matches)
Alpine.store('activeDesktopSubmenu', '')

// Define transition options for menu animations
Alpine.store('menuTransitions', {
  // The whole mobile menu when toggled
  mobileMenuContainer: {
    css: {
      opacity: 0,
      transform: 'translateY(-5px)',
    },
    duration: 300,

    get bind() {
      return Alpine.store('menu')
    },
    set bind(value) {
      Alpine.store('menu', value)
      Alpine.store('secondaryMenu', false)
      Alpine.store('activeDesktopSubmenu', '')
    },
  },

  // A submenu expanding/collapsing on mobile
  mobileSubmenu: {
    css: {
      height: 0,
      marginTop: 0,
      marginBottom: 0,
      paddingTop: 0,
      paddingBottom: 0,
      opacity: 0,
    },
    duration: 300,

    // Hide overflow during animation to properly visualize height transition
    onBeforeAnimate(element) {
      element.style.overflow = 'hidden'
    },
    onComplete(element) {
      element.style.removeProperty('overflow')
    },
  },

  // A submenu appearing/disappearing on desktop
  desktopSubmenu: id => ({
    css: {
      opacity: 0,
      transform: 'translateY(-20px)',
    },
    duration: 200,

    // Avoid pointer interactivity to avoid accidental closing/re-opening during transition
    onBeforeAnimate(element) {
      element.style.pointerEvents = 'none'
    },
    onComplete(element) {
      element.style.removeProperty('pointer-events')
    },

    get bind() {
      return Alpine.store('activeDesktopSubmenu') === id
    },
    set bind(value) {
      if (value) {
        Alpine.store('activeDesktopSubmenu', id)
      } else {
        Alpine.store('activeDesktopSubmenu', '')
      }
    },
  }),

  mobileSecondaryMenuContainer: {
    css: {
      opacity: 0,
      transform: 'translateX(20px)',
    },
    duration: 300,

    get bind() {
      return Alpine.store('secondaryMenu')
    },
    set bind(value) {
      Alpine.store('secondaryMenu', value)
      Alpine.store('menu', false)
      Alpine.store('activeDesktopSubmenu', '')
    },
  },

  search: {
    css: {
      opacity: 0,
      transform: 'translateY(-20px)',
    },
    duration: 200,

    // Avoid pointer interactivity to avoid accidental closing/re-opening during transition
    onBeforeAnimate(element) {
      element.style.pointerEvents = 'none'
    },
    onComplete(element) {
      element.style.removeProperty('pointer-events')
    },

    get bind() {
      return Alpine.store('activeDesktopSubmenu') === 'search' && Alpine.store('menu') === false
    },
    set bind(value) {
      if (value) {
        Alpine.store('activeDesktopSubmenu', 'search')
        Alpine.store('menu', false)
        Alpine.store('secondaryMenu', false)
      } else {
        Alpine.store('activeDesktopSubmenu', '')
      }
    },
  },
})

// We'll attach the Alpine menu logic via JS because it would be *very* tedious
// to do so via the various hooks WordPress provides for menu HTML generation.
// A non-JS fallback is ensured through pure CSS.
document.querySelectorAll('nav.main').forEach(menuNavContainer => {
  let isMobile = menuNavContainer.matches('.mobile-menu-wrapper *')

  // Close mobile menu when clicking an anchor link
  menuNavContainer.querySelectorAll('.menu-item a').forEach(menuItemLink => {
    if (
      isMobile &&
      menuItemLink.getAttribute('href') !== '#' &&
      menuItemLink.getAttribute('href').startsWith('#')
    ) {
      menuItemLink.setAttribute('x-on:click', '$store.menu = false')
    }
  })

  // Ignore anchor links in submenus
  menuNavContainer.querySelectorAll('.sub-menu .menu-item a').forEach(menuItemLink => {
    if (!isMobile && menuItemLink.getAttribute('href') === '#') {
      menuItemLink.removeAttribute('href')
      menuItemLink.setAttribute('role', 'presentation')
    }
  })

  // Iterate all list items that have submenus
  menuNavContainer
    .querySelectorAll('.menu-item-has-children:not(.sub-menu *)')
    .forEach(menuItem => {
      let submenuList = menuItem.querySelector('.sub-menu')

      let menuItemLink = menuItem.querySelector('a')

      if (isMobile) {
        /*******************************
         * Mobile menu specific functionality
         ******************************/

        // Make submenu focusable as a whole and hide it
        submenuList.setAttribute('x-bind', 'target')
        submenuList.setAttribute('tabindex', '-1')

        // Hide submenu unless it's the current menu item
        if (!menuItem.classList.contains('current-menu-parent')) {
          submenuList.style.display = 'none'
        }

        // Add unique ID to submenu for ARIA markup
        let menuItemIdClass = [...menuItem.classList].find(className =>
          /^menu-item-[0-9]+$/.test(className),
        )
        let submenuId = menuItemIdClass
          ? `submenu-${menuItemIdClass.replace('menu-item-', '')}`
          : `submenu-${uuid()}`

        submenuList.setAttribute('id', submenuId)

        // Disable link clickability if it points to empty anchor
        // Make it show the submenu instead
        if (menuItemLink.getAttribute('href') === '#') {
          menuItemLink.setAttribute('x-on:click.prevent', 'toggle()')
        }

        menuItem.setAttribute('x-data', 'transition($store.menuTransitions.mobileSubmenu)')

        // Handle dropdown behavior
        menuItemLink.setAttribute('role', 'button')
        menuItemLink.setAttribute('x-on:click.prevent', 'toggle()')
        menuItemLink.setAttribute('x-on:keydown.space.prevent', 'toggle()')
        menuItemLink.setAttribute('id', 'isVisible()')
        menuItemLink.setAttribute(':aria-expanded', 'isVisible()')
        menuItemLink.setAttribute(':aria-controls', JSON.stringify(submenuId))
      } else {
        /*******************************
         * Desktop menu specific functionality
         ******************************/

        // Add unique ID to submenu wrapper for ARIA markup
        let menuItemIdClass = [...menuItem.classList].find(className =>
          /^menu-item-[0-9]+$/.test(className),
        )
        let submenuId = menuItemIdClass
          ? `submenu-${menuItemIdClass.replace('menu-item-', '')}`
          : `submenu-${uuid()}`

        let wrapper = fragment`<div
            class="sub-menu-wrapper"
            x-bind="target"
            x-trap="isVisible()"
            @keydown.escape="hide()"
            style="display: none"
          ></div>`
        wrapper.setAttribute
        wrapper.setAttribute('id', submenuId)
        let wrapper2 = fragment`<div class="sub-menu-wrapper-inner">
            <button class="submenu-close-button" type="button" @click="hide()">
              <svg aria-hidden="true" focusable="false" width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M15 14.32 29.32 0l.68.68L15.68 15 30 29.32l-.68.68L15 15.68.68 30 0 29.32 14.32 15 0 .68.68 0 15 14.32Z" fill="currentColor"/></svg>
              <span class="sr-only">Untermenü schliessen</span>
            </button>
          </div>`
        wrapper.appendChild(wrapper2)
        submenuList.after(wrapper)
        wrapper2.appendChild(submenuList)

        menuItem.setAttribute(
          'x-data',
          `transition($store.menuTransitions.desktopSubmenu(${JSON.stringify(submenuId)}))`,
        )

        // Handle dropdown behavior
        menuItemLink.setAttribute('role', 'button')
        menuItemLink.setAttribute('x-on:click.prevent', 'toggle()')
        menuItemLink.setAttribute('x-on:keydown.space.prevent', 'toggle()')
        menuItemLink.setAttribute(':aria-expanded', 'isVisible()')
        menuItemLink.setAttribute(':aria-controls', JSON.stringify(submenuId))
        menuItem.setAttribute(':style', '{ zIndex: latestEl === $el ? zIndex : null }')
      }
    })
})
